import { captureException } from '@sentry/react';
import { useCallback, useMemo } from 'react';

import useInvoice from '~/data/hooks/useInvoice';
import useInvoices from '~/data/hooks/useInvoices';
import type { Invoice } from '~/data/schemas/invoice';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';

/**
 * Linking and unlinking other invoices/proposals on an invoice, plus the property's
 * invoices as pickable options. The API takes the full related-invoice list on every
 * write, so add and remove are both "send the new list" — which is why this belongs in
 * one place rather than being reimplemented per surface.
 */
export function useRelatedInvoiceLinking(invoice: Invoice | undefined) {
  const { showEntityActionSnackbar } = useLAISnackbar();
  const propertyId = invoice?.property?.id;

  // TODO: We should probably filter by the search input
  const { invoices: invoiceList } = useInvoices(
    { property: propertyId, variant: 'form' },
    propertyId !== undefined,
  );

  const linkedIds = useMemo(
    () => (invoice?.relatedInvoices ?? []).map((related) => related.id),
    [invoice?.relatedInvoices],
  );

  // Already-linked invoices, and the invoice itself, are not offerable.
  const invoiceOptions = useMemo(
    () =>
      invoiceList
        ?.filter((option) => !linkedIds.includes(option.id) && option.id !== invoice?.id)
        .map((option) => ({ id: option.id, label: option.number })),
    [invoiceList, linkedIds, invoice?.id],
  );

  const { updateRelatedInvoice } = useInvoice(invoice?.id ?? null);

  // `updateRelatedInvoice` throws on a failed write and the table row simply stays where
  // it was, which reads as "nothing happened". Say so, then rethrow so the caller can keep
  // the picked number in the field for a retry.
  const writeRelatedInvoices = useCallback(
    async (relatedIds: string[]) => {
      try {
        await updateRelatedInvoice(relatedIds);
      } catch (error) {
        captureException(error);
        showEntityActionSnackbar(
          { name: 'Invoice', action: ActionEnum.Fail, id: undefined },
          { variant: 'error' },
        );
        throw error;
      }
    },
    [updateRelatedInvoice, showEntityActionSnackbar],
  );

  const onAddRelatedInvoice = useCallback(
    async (invoiceId: string) => {
      await writeRelatedInvoices([...linkedIds, invoiceId]);
    },
    [writeRelatedInvoices, linkedIds],
  );

  const onRemoveRelatedInvoice = useCallback(
    async (invoiceId: string) => {
      await writeRelatedInvoices(linkedIds.filter((id) => id !== invoiceId));
    },
    [writeRelatedInvoices, linkedIds],
  );

  return { invoiceOptions, onAddRelatedInvoice, onRemoveRelatedInvoice };
}
